import React, { useMemo } from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import {
  ReportField,
  ReportFlowPages,
  ReportFooter,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportNote,
  ReportSignLine,
  ReportSigns,
  ReportTitleRow,
} from '../../../components/ui/Report';
import type { CrewStatsAvailableRow } from '../types';

function dash(v: React.ReactNode) {
  if (v == null || v === '') return '—';
  return v;
}

function fmtDate(v?: string | null) {
  return v ? formatDateGlobal(v) : '—';
}

function sortAvailableRows(rows: CrewStatsAvailableRow[]): CrewStatsAvailableRow[] {
  return [...rows].sort((a, b) => {
    const ao = a.rankSortOrder ?? 9999;
    const bo = b.rankSortOrder ?? 9999;
    if (ao !== bo) return ao - bo;
    return a.name.localeCompare(b.name);
  });
}

function TableHead() {
  return (
    <table className="crew-list-print-table so-flow-table so-flow-table-head sa-flow-table">
      <thead>
        <tr>
          <th className="crew-list-print-col-no">No.</th>
          <th className="so-flow-col-name">Name</th>
          <th className="so-flow-col-rank">Rank</th>
          <th className="so-flow-col-nat">Nationality</th>
          <th className="so-flow-col-date">Av. from</th>
          <th className="so-flow-col-date">Av. to</th>
          <th className="sa-flow-col-status">Av. status</th>
          <th className="so-flow-col-vessel">Last vessel</th>
          <th className="so-flow-col-rank">Last rank</th>
        </tr>
      </thead>
    </table>
  );
}

function LineRow({ row, index }: { row: CrewStatsAvailableRow; index: number }) {
  return (
    <table className="crew-list-print-table so-flow-table so-flow-row-table sa-flow-table">
      <tbody>
        <tr>
          <td className="crew-list-print-col-no">{index + 1}</td>
          <td className="so-flow-col-name">{row.name}</td>
          <td className="so-flow-col-rank">{dash(row.rank)}</td>
          <td className="so-flow-col-nat">{dash(row.nationality)}</td>
          <td className="so-flow-col-date">{fmtDate(row.availableFrom)}</td>
          <td className="so-flow-col-date">{fmtDate(row.availableTo)}</td>
          <td className="sa-flow-col-status">{dash(row.availabilityStatus)}</td>
          <td className="so-flow-col-vessel">{dash(row.lastVessel)}</td>
          <td className="so-flow-col-rank">{dash(row.lastRank)}</td>
        </tr>
      </tbody>
    </table>
  );
}

export type StatsAvailableReportProps = {
  rows: CrewStatsAvailableRow[];
  vesselLabel: string;
};

export const StatsAvailableReport: React.FC<StatsAvailableReportProps> = ({
  rows,
  vesselLabel,
}) => {
  const sortedRows = useMemo(() => sortAvailableRows(rows), [rows]);

  const firstHeader = useMemo(
    () => (
      <div className="so-flow-header">
        <ReportHeader>
          <ReportTitleRow title="Available crew" formId="Operations" />
          <ReportMeta>
            <ReportMetaRow>
              <ReportField label="Scope" value={vesselLabel} />
              <ReportField label="Printed" value={fmtDate(new Date().toISOString().slice(0, 10))} />
              <ReportField label="Sorted by" value="Rank order" />
            </ReportMetaRow>
          </ReportMeta>
        </ReportHeader>
        <TableHead />
      </div>
    ),
    [vesselLabel],
  );

  const continueHeader = useMemo(
    () => (
      <div className="so-flow-header">
        <div className="so-flow-continue-bar">
          <span className="so-flow-continue-title">Available crew</span>
          <span className="so-flow-continue-meta">{vesselLabel}</span>
          <span className="so-flow-continue-hint">continued</span>
        </div>
        <TableHead />
      </div>
    ),
    [vesselLabel],
  );

  const lastFooter = useMemo(
    () => (
      <ReportFooter>
        <ReportSigns>
          <ReportSignLine label="Prepared by" />
          <ReportSignLine label="Date" />
        </ReportSigns>
        <ReportNote>
          Relief-pool crew with availability dates or status (excluding not-recommended), ordered by
          rank.
        </ReportNote>
      </ReportFooter>
    ),
    [],
  );

  const { items, itemKeys } = useMemo(() => {
    if (sortedRows.length === 0) {
      return {
        items: [
          <table key="empty" className="crew-list-print-table so-flow-table">
            <tbody>
              <tr>
                <td colSpan={9} className="crew-list-print-empty">
                  No available crew for these filters.
                </td>
              </tr>
            </tbody>
          </table>,
        ],
        itemKeys: ['empty'],
      };
    }
    return {
      items: sortedRows.map((row, i) => <LineRow key={row.crewId} row={row} index={i} />),
      itemKeys: sortedRows.map((row) => String(row.crewId)),
    };
  }, [sortedRows]);

  return (
    <ReportFlowPages
      className="crew-list-print-report crew-list-print-stats-onboard"
      size="portrait"
      firstHeader={firstHeader}
      continueHeader={continueHeader}
      lastFooter={lastFooter}
      items={items}
      itemKeys={itemKeys}
    />
  );
};

export default StatsAvailableReport;
